import "./AdminConsole.css";

import React, { useEffect, useRef, useState } from "react";
import {
  deleteBriefing,
  getBriefing,
  sendBriefingEmail,
  sendBriefingEmailToPecificUsers,
} from "../../services/briefingService";
import {
  deleteStory,
  getAdminStories,
  getApprovedRejectStories,
  getUserInDevsStories,
  updateStoryStatus,
  updateStoryType,
} from "../../services/storyService";

import Briefing from "./AdminConsoleContent/briefings/Briefing";
import ColumnNotification from "./AdminConsoleContent/ColumnNotification";
import CreateBriefing from "./AdminConsoleContent/briefings/CreateBriefing";
import CreateStory from "./AdminConsoleContent/stories/CreateStory";
import ExportStories from "./AdminConsoleContent/ExportStories";
import PublishBriefing from "./AdminConsoleContent/briefings/PublishBriefing";
import SendBriefing from "./AdminConsoleContent/SendBriefing/SendBriefing";
import SendingStatistics from "./AdminConsoleContent/SendingStatistics";
import Stories from "./AdminConsoleContent/stories/Stories";
import StoryDetail from "./AdminConsoleContent/stories/StoryDetail/StoryDetail";
import UploadPodcast from "./AdminConsoleContent/UploadPodcast";
import { selectUser } from "../../redux/reducers/userReducer";
import { useLoader } from "../../hooks/useLoader";
import { useLogoutRedirectionHome } from "../../hooks/useLogoutRedirectionHome";
import { useOnlyAdminUsers } from "../../hooks/useOnlyAdminUsers";
import { useSelector } from "react-redux";

const enum ADMIN_PAGES {
  STORIES = "stories",
  BRIEFING = "briefing",
  PUBLISH_BRIEFING = "publish-briefing",
  COLUMN = "column",
  SEND_BRIEFING = "send-briefing",
  SENDING_STATISTICS = "sending-statistics",
  CREATE_BRIEFING = "create-briefing",
  CREATE_STORY = "create-story",
  STORY_DETAIL = "story-detail",
  UPLOAD_PODCAST = "upload-podcast",
  EXPORT_STORIES = "export-stories",
}

const AdminConsole = () => {
  const [activeTopic, setActiveTopic] = useState(ADMIN_PAGES.STORIES);
  //stories
  const [allStoriesData, setAllStoriesData] = useState([]);
  const [allApprovedRejectedStories, setApprovedRejectedStories] = useState([]);
  const [allInDevsStories, setAllInDevsStories] = useState([]);

  const [allBriefings, setAllBriefing] = useState([]);

  const selectedBriefing = useRef(null);
  const selectedStory = useRef(null);

  const user = useSelector(selectUser);

  useOnlyAdminUsers();
  useLogoutRedirectionHome();

  const { showLoading } = useLoader();

  useEffect(() => {
    initialCalls();
  }, [user?.data?.user?.user_id]);

  // Keep presence badges in the Editing tab live: silently re-fetch the
  // in-dev list every 30s (no global spinner — this must be invisible).
  useEffect(() => {
    const email = user?.data?.user?.user_id;
    if (!email) return;
    const interval = setInterval(async () => {
      try {
        const res = await getUserInDevsStories({ email });
        setAllInDevsStories(res?.data?.data || []);
      } catch (error) {
        // transient poll failure — badges just stay as they were
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [user?.data?.user?.user_id]);

  const initialCalls = () => {
    getStoriesFromServer();
    getInDevStoriesFromServer();
    getApprovedRejectStoriesFromServer();
    //briefing
    getBriefingFromServer();
  };

  //api calls
  const getBriefingFromServer = async () => {
    try {
      showLoading(true);
      const res = await getBriefing({
        email: user.data?.user.user_id || "",
      });
      setAllBriefing(res?.data?.data || []);
      showLoading(false);
    } catch (error) {
      showLoading(false);
    }
  };

  const getStoriesFromServer = async () => {
    try {
      showLoading(true);
      const res = await getAdminStories({
        email: user.data?.user.user_id || "",
      });
      setAllStoriesData(res?.data?.data || []);
      showLoading(false);
    } catch (error) {
      showLoading(false);
    }
  };

  const getInDevStoriesFromServer = async () => {
    try {
      showLoading(true);
      const res = await getUserInDevsStories({
        email: user.data?.user.user_id || "",
      });
      setAllInDevsStories(res?.data?.data || []);
      showLoading(false);
    } catch (error) {
      showLoading(false);
    }
  };

  const getApprovedRejectStoriesFromServer = async () => {
    try {
      showLoading(true);
      const res = await getApprovedRejectStories({
        email: user.data?.user.user_id || "",
        approverEmail: user.data?.user.user_id || "",
      });
      setApprovedRejectedStories(res?.data?.data || []);
      showLoading(false);
    } catch (error) {
      showLoading(false);
    }
  };

  // Editors pick the lead (public) story after all stories are filed: promote
  // the chosen story to public and demote the briefing's current lead (if any)
  // to subscriber-only. Uses the lean editStory type update.
  const makeLeadStory = async (story: any) => {
    const currentLead = allInDevsStories.find(
      (s: any) =>
        s.briefingId === story.briefingId &&
        s.type === "public" &&
        s.id !== story.id
    );
    const msg = currentLead
      ? `Make "${story.heading}" the lead (public) story?\n\n"${currentLead.heading}" will become subscriber-only.`
      : `Make "${story.heading}" the lead (public) story?`;
    if (!window.confirm(msg)) return;
    try {
      showLoading(true);
      if (currentLead) {
        await updateStoryType({
          storyId: currentLead.id,
          briefingId: currentLead.briefingId || "",
          email: user.data?.user.user_id || "",
          type: "subscriber only",
        });
      }
      await updateStoryType({
        storyId: story.id,
        briefingId: story.briefingId || "",
        email: user.data?.user.user_id || "",
        type: "public",
      });
      await getInDevStoriesFromServer();
      showLoading(false);
    } catch (error) {
      showLoading(false);
      alert("Failed to update the lead story. Please try again.");
    }
  };

  const updateStoryStatusToServer = async (story, status) => {
    try {
      showLoading(true);
      const res = await updateStoryStatus({
        email: user.data?.user.user_id || "",
        storyId: story.id,
        status: status,
        approverEmail: user.data?.user.user_id || "",
        approverName: user.data?.user.name || "",
      });
      setApprovedRejectedStories(res?.data?.data || []);
      showLoading(false);
      backPress();
    } catch (error) {
      showLoading(false);
    }
  };

  const sendEmailApi = async (briefingId: string) => {
    try {
      showLoading(true);
      await sendBriefingEmail({ briefingId });
      alert("Email sent successfully!!");
    } catch (e) {
      alert("Something went wrong, please try again.");
    } finally {
      showLoading(false);
    }
  };

  const sendBriefingEmailToPecificUsersApi = async (
    briefingId: string,
    userIds: string[]
  ) => {
    try {
      showLoading(true);
      const data = await sendBriefingEmailToPecificUsers({
        briefingId,
        userIds,
      });
      alert("Email sent successfully!!");
    } catch (e) {
      alert("Something went wrong, please try again.");
    } finally {
      showLoading(false);
    }
  };

  const handleDeleteStory = async (story: any) => {
    const confirm = window.confirm("Are you sure want to delete?");

    if (confirm) {
      try {
        showLoading(true);
        const res = await deleteStory({
          storyId: story.id,
          email: user.data?.user.user_id || "",
        });
        setAllStoriesData(
          allStoriesData.filter((item: any) => item.id !== story.id)
        );
        showLoading(false);
      } catch (error) {
        alert("Something went wrong, Try again!");
        showLoading(false);
      }
    }
  };

  const handleDeleteBriefing = async (briefing: any) => {
    const confirm = window.confirm("Are you sure want to delete?");

    if (confirm) {
      try {
        showLoading(true);
        const res = await deleteBriefing({
          briefingId: briefing.id,
          email: user.data?.user.user_id || "",
        });
        setAllBriefing(
          allBriefings.filter((item: any) => item.id !== briefing.id)
        );
        showLoading(false);
      } catch (error) {
        alert("Something went wrong, Try again!");
        showLoading(false);
      }
    }
  };

  const storyDetailsClick = (story: any) => {
    selectedStory.current = story;
    setActiveTopic(ADMIN_PAGES.STORY_DETAIL);
  };

  const backPress = () => {
    setActiveTopic(ADMIN_PAGES.STORIES);
    initialCalls();
  };

  const renderContent = () => {
    switch (activeTopic) {
      case ADMIN_PAGES.STORIES:
        return (
          <Stories
            allStoriesData={allStoriesData}
            createStoryClick={(story: any) => {
              selectedStory.current = story;
              setActiveTopic(ADMIN_PAGES.CREATE_STORY);
            }}
            handleDeleteStory={handleDeleteStory}
            allApprovedRejectedStories={allApprovedRejectedStories}
            allInDevsStories={allInDevsStories}
            storyDetailsClick={storyDetailsClick}
            updateStoryStatusToServer={updateStoryStatusToServer}
            makeLeadClick={makeLeadStory}
          />
        );
      case ADMIN_PAGES.BRIEFING:
        return (
          <Briefing
            allBriefings={allBriefings}
            handleDeleteBriefing={handleDeleteBriefing}
            createBriefingClick={(briefing: any) => {
              selectedBriefing.current = briefing;
              setActiveTopic(ADMIN_PAGES.CREATE_BRIEFING);
            }}
            onBrifingClick={(briefing: any) => {
              selectedBriefing.current = briefing;
              setActiveTopic(ADMIN_PAGES.PUBLISH_BRIEFING);
            }}
          />
        );
      case ADMIN_PAGES.PUBLISH_BRIEFING:
        return (
          <PublishBriefing
            briefing={selectedBriefing.current}
            backPress={backPress}
          />
        );
      // case ADMIN_PAGES.SEND_BRIEFING:
      //   return <SendBriefing />;
      case ADMIN_PAGES.SENDING_STATISTICS:
        return <SendingStatistics />;
      case ADMIN_PAGES.COLUMN:
        return (
          <ColumnNotification
            allBriefings={allBriefings}
            sendEmailApi={sendEmailApi}
            sendBriefingEmailToPecificUsersApi={
              sendBriefingEmailToPecificUsersApi
            }
          />
        );
      case ADMIN_PAGES.CREATE_BRIEFING:
        return (
          <CreateBriefing
            briefing={selectedBriefing.current}
            backPress={backPress}
          />
        );
      case ADMIN_PAGES.CREATE_STORY:
        return (
          <CreateStory
            story={selectedStory.current}
            backPress={backPress}
            allBriefings={allBriefings}
          />
        );
      case ADMIN_PAGES.STORY_DETAIL:
        return (
          <StoryDetail
            story={selectedStory.current}
            updateStoryStatusToServer={updateStoryStatusToServer}
            makeLeadClick={makeLeadStory}
          />
        );
      case ADMIN_PAGES.UPLOAD_PODCAST:
        return <UploadPodcast />;
      case ADMIN_PAGES.EXPORT_STORIES:
        return <ExportStories />;
      default:
        return null;
    }
  };

  const isStoriesActive = () => {
    return (
      activeTopic === ADMIN_PAGES.STORIES ||
      activeTopic === ADMIN_PAGES.STORY_DETAIL ||
      activeTopic === ADMIN_PAGES.CREATE_STORY
    );
  };

  const isBriefingsActive = () => {
    return (
      activeTopic === ADMIN_PAGES.BRIEFING ||
      activeTopic === ADMIN_PAGES.CREATE_BRIEFING
    );
  };

  return (
    <div className="admin-console">
      <nav className="sidebar-admin">
        <ul>
          <li
            style={{
              backgroundColor: isStoriesActive() ? "#5C7693" : "transparent",
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.STORIES)}
          >
            <img src="/assets/stories_side_menu_ic.svg" alt="StoriesIcon" />
            Stories
          </li>
          <li
            style={{
              backgroundColor: isBriefingsActive() ? "#5C7693" : "transparent",
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.BRIEFING)}
          >
            <img src="/assets/briefings_side_menu_ic.svg" alt="BriefingIcon" />
            Briefing
          </li>
          {/* <li
            style={{
              backgroundColor:
                activeTopic === ADMIN_PAGES.SEND_BRIEFING
                  ? '#5C7693'
                  : 'transparent',
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.SEND_BRIEFING)}
          >
            <img
              src='/assets/send_briefing_side_menu_ic.svg'
              alt='SendBriefingIcon'
            />
            Send Briefing
          </li> */}
          <li
            style={{
              backgroundColor:
                activeTopic === ADMIN_PAGES.COLUMN ? "#5C7693" : "transparent",
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.COLUMN)}
          >
            <img src="/assets/ColumnNotification.png" alt="NotificationIcon" />
            Send Briefing
          </li>
          <li
            style={{
              backgroundColor:
                activeTopic === ADMIN_PAGES.SENDING_STATISTICS
                  ? '#5C7693'
                  : 'transparent',
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.SENDING_STATISTICS)}
          >
            <img src='/assets/SendBriefing.png' alt='SendStatisticsIcon' />
            Sending Statistics
          </li>
          <li
            style={{
              backgroundColor:
                activeTopic === ADMIN_PAGES.UPLOAD_PODCAST
                  ? "#5C7693"
                  : "transparent",
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.UPLOAD_PODCAST)}
          >
            <img src="/assets/SendBriefing.png" alt="SendStatisticsIcon" />
            Upload Podcast
          </li>
          <li
            style={{
              backgroundColor:
                activeTopic === ADMIN_PAGES.EXPORT_STORIES
                  ? "#5C7693"
                  : "transparent",
            }}
            onClick={() => setActiveTopic(ADMIN_PAGES.EXPORT_STORIES)}
          >
            <img src="/assets/stories_side_menu_ic.svg" alt="ExportIcon" />
            Export Stories
          </li>
        </ul>
      </nav>
      <div className="content-admin">{renderContent()}</div>
    </div>
  );
};

export default AdminConsole;
